import * as React from 'react'

import { Box, Button } from '@factorialco/deprecated-design-system'

import Modal from 'modules/core/components/Modal'
import TextField from 'modules/core/components/TextField'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { handleResult } from 'modules/core/lib/handleMutation'
import hasFeature from 'modules/core/lib/hasFeature'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import getTitle from 'modules/documents/components/Documents/getTitle'
import { Folder } from 'modules/documents/hooks/useGetFolders'
import { useUpdateFolder } from 'modules/documents/hooks/useUpdateFolder'
import { ALL_TYPES } from 'modules/documents/lib/constants'
import FeatureModalChecker, {
  CHECKED_FEATURES,
} from 'modules/timeoff/components/FeatureModalChecker'

interface FolderFormValues {
  name: string
}
interface Props {
  folder: Folder
  onClose: () => void
  companyId: string
}

const EditFolderModal = ({ folder, onClose, companyId }: Props) => {
  const { updateFolder } = useUpdateFolder()
  const initialFolderName = React.useMemo(() => {
    return folder.name
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const name = getTitle(folder.name)

  const form = useForm<FolderFormValues>({
    defaultValues: {
      name: name,
    },
    validate: (data) => {
      const errors: FormErrors<FolderFormValues> = {}
      if (!data.name) {
        errors.name = i18n.t('common.empty')
      }
    },
    onSubmit: async (data, { setErrors }) => {
      const result = await updateFolder({
        companyId: companyId,
        name: data.name,
        id: folder.id,
      })

      const handled = handleResult(result, ({ name }) => {
        setErrors({
          name,
        })
      })

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
        })
        onClose()
      }

      return handled
    },
  })

  const canEditFolder = () => {
    if (folder.isReadonly || !folder.type) return false

    return [ALL_TYPES.custom, ALL_TYPES.custom_company, ALL_TYPES.custom_company_public].includes(
      folder.type
    )
  }

  const disabled =
    form.isSubmitting || !form.isValid || !canEditFolder() || !hasFeature('custom_folders')

  return (
    <FeatureModalChecker feature={CHECKED_FEATURES.create_custom_folders!} onClose={onClose}>
      <Modal
        illustration="pencil"
        title={i18n.t('settings.folders.form.rename.title')}
        description={i18n.t('settings.folders.form.rename.description', {
          folder_name: initialFolderName,
        })}
        onClose={onClose}
        onSubmit={() => form.handleSubmit()}
        footer={
          <Box gap="s4" flexDirection="row">
            <Button onClick={onClose} type={'secondary'}>
              {i18n.t('settings.folders.form.rename.cancel')}
            </Button>
            <Button disabled={disabled} type="negative" submit>
              {i18n.t('settings.folders.form.rename.button')}
            </Button>
          </Box>
        }
      >
        <TextField
          label={i18n.t('settings.folders.folder.name')}
          value={form.values.name}
          onChange={(value) => form.setValues({ ...form.values, name: value })}
          error={form.touched.name ? form.errors.name : undefined}
          autofocus
        ></TextField>
      </Modal>
    </FeatureModalChecker>
  )
}

export default EditFolderModal
